import React from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useGetNotificationByIdQuery } from "../api/notificationApi";
import { formatDateToYMD } from "../../../core/utils/dateFormatter";

const NotificationFeedback = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();

  // Fetch notification data
  const {
    data: notificationData,
    isLoading,
    isError,
  } = useGetNotificationByIdQuery({ id: id || "" }, { skip: !id });

  const notification = notificationData?.data?.notification;

  const feedbacks = React.useMemo(() => {
    if (!notification?.feedbacks) return [];
    return Array.isArray(notification.feedbacks)
      ? notification.feedbacks
      : Object.values(notification.feedbacks);
  }, [notification]);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Notification Feedbacks</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.notificationList}>Notifications</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Feedbacks
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="mb-2">
              <Link
                to={routes.notificationList}
                className="btn btn-outline-primary d-flex align-items-center"
              >
                <i className="ti ti-arrow-left me-2"></i>
                Back to List
              </Link>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title mb-1">
                    Feedbacks for: {notification?.title || "Loading..."}
                  </h4>
                  {notification?.publishedOn && (
                    <p className="text-muted mb-0 small">
                      Published on: {formatDateToYMD(notification.publishedOn)}
                    </p>
                  )}
                </div>
                <span className="badge badge-soft-primary">
                  {feedbacks.length} Responses
                </span>
              </div>
              <div className="card-body">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Fetching all feedbacks...</p>
                  </div>
                ) : isError ? (
                  <div className="alert alert-danger">
                    Error loading feedbacks. Please try again.
                  </div>
                ) : feedbacks.length > 0 ? (
                  <div className="feedback-timeline mt-2">
                    {feedbacks.map((feedback, index) => (
                      <div key={feedback.id} className="feedback-card border rounded p-3 mb-3 hover-shadow-sm transition-all bg-white">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="d-flex align-items-center">
                            <div
                              className="avatar avatar-md bg-soft-primary text-primary rounded-circle me-3 d-flex align-items-center justify-content-center fw-bold"
                              style={{ width: "40px", height: "40px", fontSize: "16px" }}
                            >
                              {feedback.feedback_by?.[0]?.toUpperCase() || "C"}
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">{feedback.feedback_by}</h6>
                              <div className="d-flex align-items-center gap-2 mt-1">
                                <span className={`badge bg-light-info text-info border-info border-1 text-uppercase`} style={{ fontSize: "10px" }}>
                                  {feedback.role}
                                </span>
                                <small className="text-muted">
                                  <i className="ti ti-calendar-event me-1"></i>
                                  {new Date(feedback.feedback_at).toLocaleString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="feedback-message-box p-3 bg-light rounded shadow-inner">
                          <p className="mb-0 text-dark" style={{ lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                            {feedback.message}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <span className="avatar avatar-xxl bg-soft-secondary rounded-circle">
                        <i className="ti ti-message-off fs-1 text-muted"></i>
                      </span>
                    </div>
                    <h5 className="text-muted">No Feedbacks Found</h5>
                    <p className="text-muted">Once centers provide feedback, they will appear here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationFeedback;
